import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ListTree, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { CategoryTreeNode } from "@/services/category.service";
import { buildSearchIndex, searchCategories } from "@/lib/categories/category-search";
import { ancestorsOf, findNode, flattenVisible } from "@/lib/categories/tree-model";
import { localizedText } from "@/lib/categories/localization";

interface CategoryFilterPopoverProps {
  readonly nodes: readonly CategoryTreeNode[];
  /** Rolled-up totals from rollUpCounts; empty while counts load. */
  readonly counts: ReadonlyMap<string, number>;
  readonly selectedId: string | null;
  readonly onSelect: (categoryId: string | null) => void;
}

const formatCount = (value: number | undefined) =>
  value === undefined ? "" : new Intl.NumberFormat().format(value);

export function CategoryFilterPopover({
  nodes,
  counts,
  selectedId,
  onSelect,
}: CategoryFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const tree = nodes as CategoryTreeNode[];
  const selected = selectedId ? findNode(tree, selectedId) : null;

  // Opening on an active filter should reveal it rather than make the user
  // hunt for it down a collapsed branch.
  useEffect(() => {
    if (!open || !selectedId) return;
    setExpanded((current) => {
      const next = new Set(current);
      for (const ancestor of ancestorsOf(tree, selectedId)) next.add(ancestor.id);
      return next;
    });
  }, [open, selectedId, tree]);

  const searchIndex = useMemo(() => buildSearchIndex(tree), [tree]);
  const results = useMemo(
    () => (query.trim() ? searchCategories(searchIndex, query) : []),
    [searchIndex, query],
  );

  const rows = useMemo(() => flattenVisible(tree, expanded), [tree, expanded]);

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const choose = (id: string) => {
    onSelect(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-9 max-w-[240px] gap-2 font-medium"
          aria-label={selected ? `Category: ${localizedText(selected.name)}` : "Categories"}
        >
          <ListTree className="size-4 shrink-0" aria-hidden />
          <span className="truncate">Categories</span>
          <ChevronDown className="size-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[380px] p-0">
        {selected ? (
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 border-b px-3 py-2.5 text-left text-sm hover:bg-muted/60"
          >
            <X className="size-4 shrink-0" aria-hidden />
            <span className="truncate">
              Clear filter: <span className="font-medium">{localizedText(selected.name)}</span>
            </span>
          </button>
        ) : null}

        <div className="border-b p-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 opacity-50"
              aria-hidden
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search categories"
              aria-label="Search categories"
              className="h-8 pl-8"
            />
          </div>
        </div>

        <div className="max-h-[320px] overflow-y-auto py-1" role="tree">
          {tree.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No categories yet
            </p>
          ) : query.trim() ? (
            results.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                No categories match “{query.trim()}”
              </p>
            ) : (
              results.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  role="treeitem"
                  aria-selected={entry.id === selectedId}
                  onClick={() => choose(entry.id)}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted/60",
                    entry.id === selectedId && "bg-muted font-medium",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {entry.name}
                    {entry.path.length ? (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {entry.path.join(" › ")}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                    {formatCount(counts.get(entry.id))}
                  </span>
                </button>
              ))
            )
          ) : (
            rows.map(({ node, depth, pos, size }) => {
              const hasChildren = node.children.length > 0;
              const isOpen = expanded.has(node.id);
              return (
                <div
                  key={node.id}
                  role="treeitem"
                  aria-selected={node.id === selectedId}
                  aria-expanded={hasChildren ? isOpen : undefined}
                  aria-level={depth + 1}
                  aria-posinset={pos}
                  aria-setsize={size}
                  className={cn(
                    "flex items-center gap-1 pr-3 text-sm hover:bg-muted/60",
                    node.id === selectedId && "bg-muted font-medium",
                  )}
                  style={{ paddingLeft: `${8 + depth * 16}px` }}
                >
                  {hasChildren ? (
                    <button
                      type="button"
                      onClick={() => toggle(node.id)}
                      aria-label={`${isOpen ? "Collapse" : "Expand"} ${localizedText(node.name)}`}
                      className="shrink-0 rounded p-1 hover:bg-muted"
                    >
                      <ChevronRight
                        className={cn("size-3.5 transition-transform", isOpen && "rotate-90")}
                        aria-hidden
                      />
                    </button>
                  ) : (
                    <span className="w-[22px] shrink-0" aria-hidden />
                  )}
                  <button
                    type="button"
                    onClick={() => choose(node.id)}
                    className="min-w-0 flex-1 truncate py-1.5 text-left"
                  >
                    {localizedText(node.name)}
                  </button>
                  <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                    {formatCount(counts.get(node.id))}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
